"use client";

import { useDebounce } from "@workspace/ui/hooks/use-debounce";
import { useCallback, useEffect, useState } from "react";

export interface UseSearchInputOptions {
	initialValue?: string;
	debounceMs?: number;
	minLength?: number;
	onDebouncedValueChange?: (value: string) => void;
}

export interface UseSearchInputReturn {
	value: string;
	debouncedValue: string;
	setValue: (value: string) => void;
	handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	handleClear: () => void;
	isSearching: boolean;
}

/**
 * Hook for managing search input state with debouncing.
 * Provides a consistent pattern for search functionality across the app.
 *
 * @example
 * ```tsx
 * const search = useSearchInput({
 *   debounceMs: 300,
 *   onDebouncedValueChange: (value) => {
 *     // Perform search with debounced value
 *   }
 * })
 *
 * return (
 *   <SearchInput
 *     value={search.value}
 *     onChange={search.handleChange}
 *     placeholder="Search..."
 *   />
 * )
 * ```
 */
export function useSearchInput({
	initialValue = "",
	debounceMs = 300,
	minLength = 2,
	onDebouncedValueChange,
}: UseSearchInputOptions = {}): UseSearchInputReturn {
	const [value, setValue] = useState(initialValue);
	const debouncedValue = useDebounce(value, debounceMs);
	const [isSearching, setIsSearching] = useState(false);

	// Track if we're in a searching state
	useEffect(() => {
		if (value !== debouncedValue && value.length >= minLength) {
			setIsSearching(true);
		} else {
			setIsSearching(false);
		}
	}, [value, debouncedValue, minLength]);

	// Notify when debounced value changes
	useEffect(() => {
		if (onDebouncedValueChange) {
			if (debouncedValue.length >= minLength || debouncedValue.length === 0) {
				onDebouncedValueChange(debouncedValue);
			}
		}
	}, [debouncedValue, onDebouncedValueChange, minLength]);

	const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setValue(e.target.value);
	}, []);

	const handleClear = useCallback(() => {
		setValue("");
		if (onDebouncedValueChange) {
			onDebouncedValueChange("");
		}
	}, [onDebouncedValueChange]);

	return {
		value,
		debouncedValue,
		setValue,
		handleChange,
		handleClear,
		isSearching,
	};
}

/**
 * Hook for managing search input with URL persistence.
 * Syncs search state with URL query parameters using nuqs.
 *
 * @example
 * ```tsx
 * const search = useSearchInputWithUrl({
 *   paramName: 'q',
 *   debounceMs: 300,
 * })
 *
 * return (
 *   <SearchInput
 *     value={search.value}
 *     onChange={search.handleChange}
 *   />
 * )
 * ```
 */
export function useSearchInputWithUrl({
	paramName = "search",
	debounceMs = 300,
	minLength = 2,
	onDebouncedValueChange,
}: UseSearchInputOptions & { paramName?: string } = {}) {
	// Note: This would require nuqs integration
	// For now, we'll use regular state
	// In a real implementation, you'd use:
	// const [value, setValue] = useQueryState(paramName, parseAsString.withDefault(""))

	return useSearchInput({
		initialValue: "",
		debounceMs,
		minLength,
		onDebouncedValueChange,
	});
}
